import { all } from 'redux-saga/effects';

import { accountSettingsSaga } from '@/features/account/account-settings/store/account-settings.saga';
import { emailChangeSaga } from '@/features/account/email-change/store/email-change.saga';
import { notificationPreferencesSaga } from '@/features/account/notification-preferences/store/notification-preferences.saga';
import { passwordChangeSaga } from '@/features/account/password-change/store/password-change.saga';
import { securitySessionsSaga } from '@/features/account/security-sessions/store/security-sessions.saga';
import { addAdministratorSaga } from '@/features/administrator/add-administrator/store/add-administrator.saga';
import { administratorsSaga } from '@/features/administrator/administrators/store/administrators.saga';
import { editAdministratorSaga } from '@/features/administrator/edit-administrator/store/edit-administrator.saga';
import { apiKeysSaga } from '@/features/api-key/api-keys/store/api-keys.saga';
import { editApiKeySaga } from '@/features/api-key/edit-api-key/store/edit-api-key.saga';
import { confirmEmailChangeSaga } from '@/features/auth/confirm-email-change/store/confirm-email-change.saga';
import { forgotPasswordSaga } from '@/features/auth/forgot-password/store/forgot-password.saga';
import { loginSaga } from '@/features/auth/login/store/login.saga';
import { onboardingSaga } from '@/features/auth/onboarding/store/onboarding.saga';
import { resetPasswordSaga } from '@/features/auth/reset-password/store/reset-password.saga';
import { profileTypesSaga } from '@/features/classification/profile-types/store/profile-types.saga';
import { companiesSaga } from '@/features/company/companies/store/companies.saga';
import { companyDetailSaga } from '@/features/company/company-detail/store/company-detail.saga';
import { institutionalDetailSaga } from '@/features/company/institutional-detail/store/institutional-detail.saga';
import { institutionalsSaga } from '@/features/company/institutionals/store/institutionals.saga';
import { dashboardSaga } from '@/features/dashboard/dashboard/store/dashboard.saga';
import { metricsSaga } from '@/features/enrichment/metrics/store/metrics.saga';
import { sessionDetailSaga } from '@/features/enrichment/session-detail/store/session-detail.saga';
import { sessionsSaga } from '@/features/enrichment/sessions/store/sessions.saga';
import { adminFaqSaga } from '@/features/faq/admin-faq/store/admin-faq.saga';
import { clientFaqSaga } from '@/features/faq/client-faq/store/client-faq.saga';
import { addFundraisingSaga } from '@/features/fundraising/add-fundraising/store/add-fundraising.saga';
import { editFundraisingSaga } from '@/features/fundraising/edit-fundraising/store/edit-fundraising.saga';
import { fundraisingsSaga } from '@/features/fundraising/fundraisings/store/fundraisings.saga';
import { addLicenceSaga } from '@/features/licence/add-licence/store/add-licence.saga';
import { licenceDetailSaga } from '@/features/licence/licence-detail/store/licence-detail.saga';
import { licencesSaga } from '@/features/licence/licences/store/licences.saga';
import { myLicenceSaga } from '@/features/licence/my-licence/store/my-licence.saga';
import { addListSaga } from '@/features/list/add-list/store/add-list.saga';
import { clientImportAlertsSaga } from '@/features/list/client-import-alerts/store/client-import-alerts.saga';
import { discoveredLpDetailSaga } from '@/features/list/discovered-lp-detail/store/discovered-lp-detail.saga';
import { discoveredLpsSaga } from '@/features/list/discovered-lps/store/discovered-lps.saga';
import { editListSaga } from '@/features/list/edit-list/store/edit-list.saga';
import { fileImportSaga } from '@/features/list/file-import/store/file-import.saga';
import { importsSaga } from '@/features/list/imports/store/imports.saga';
import { listDetailSaga } from '@/features/list/list-detail/store/list-detail.saga';
import { listFeedbacksSaga } from '@/features/list/list-feedbacks/store/list-feedbacks.saga';
import { listsSaga } from '@/features/list/lists/store/lists.saga';
import { deactivatedProfilesSaga } from '@/features/profile/deactivated-profiles/store/deactivated-profiles.saga';
import { editProfileIdentitySaga } from '@/features/profile/edit-profile-identity/store/edit-profile-identity.saga';
import { exitsInvestmentsReviewSaga } from '@/features/profile/exits-investments-review/store/exits-investments-review.saga';
import { myProfilesSaga } from '@/features/profile/my-profiles/store/my-profiles.saga';
import { nameRulesSaga } from '@/features/profile/name-rules/store/name-rules.saga';
import { profileCleaningSaga } from '@/features/profile/profile-cleaning/store/profile-cleaning.saga';
import { profileDetailSaga } from '@/features/profile/profile-detail/store/profile-detail.saga';
import { profileExportSaga } from '@/features/profile/profile-export/store/profile-export.saga';
import { profilesSaga } from '@/features/profile/profiles/store/profiles.saga';
import { trackedLpsSaga } from '@/features/profile/tracked-lps/store/tracked-lps.saga';
import { mySchoolsSaga } from '@/features/school/my-schools/store/my-schools.saga';
import { schoolsSaga } from '@/features/school/schools/store/schools.saga';
import { addUserSaga } from '@/features/user/add-user/store/add-user.saga';
import { editUserSaga } from '@/features/user/edit-user/store/edit-user.saga';
import { usersSaga } from '@/features/user/users/store/users.saga';

/** Application root saga: forks every feature saga. One entry per slice. */
export function* rootSaga() {
  yield all([
    accountSettingsSaga(),
    dashboardSaga(),
    securitySessionsSaga(),
    passwordChangeSaga(),
    emailChangeSaga(),
    notificationPreferencesSaga(),
    forgotPasswordSaga(),
    resetPasswordSaga(),
    confirmEmailChangeSaga(),
    onboardingSaga(),
    addAdministratorSaga(),
    editAdministratorSaga(),
    administratorsSaga(),
    apiKeysSaga(),
    editApiKeySaga(),
    loginSaga(),
    addListSaga(),
    discoveredLpsSaga(),
    discoveredLpDetailSaga(),
    editListSaga(),
    fileImportSaga(),
    importsSaga(),
    addLicenceSaga(),
    licencesSaga(),
    licenceDetailSaga(),
    myLicenceSaga(),
    listsSaga(),
    listDetailSaga(),
    listFeedbacksSaga(),
    clientImportAlertsSaga(),
    profilesSaga(),
    myProfilesSaga(),
    trackedLpsSaga(),
    deactivatedProfilesSaga(),
    profileDetailSaga(),
    editProfileIdentitySaga(),
    profileExportSaga(),
    profileCleaningSaga(),
    exitsInvestmentsReviewSaga(),
    profileTypesSaga(),
    nameRulesSaga(),
    adminFaqSaga(),
    clientFaqSaga(),
    usersSaga(),
    addUserSaga(),
    editUserSaga(),
    companiesSaga(),
    schoolsSaga(),
    mySchoolsSaga(),
    companyDetailSaga(),
    institutionalsSaga(),
    institutionalDetailSaga(),
    sessionsSaga(),
    sessionDetailSaga(),
    metricsSaga(),
    fundraisingsSaga(),
    addFundraisingSaga(),
    editFundraisingSaga(),
  ]);
}
